import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

let _id = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 320);
  }, []);

  const toast = useCallback((message, type = 'error', duration = 4000) => {
    const id = ++_id;
    setToasts((prev) => [...prev, { id, message, type, exiting: false }]);
    timers.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const value = {
    toast,
    error:   (msg, dur) => toast(msg, 'error', dur),
    success: (msg, dur) => toast(msg, 'success', dur),
    info:    (msg, dur) => toast(msg, 'info', dur),
    dismiss,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

/* ── Toast Container ──────────────────────────────────────────────── */
const ICONS = {
  error:   '✕',
  success: '✓',
  info:    'i',
};

const STYLES = {
  error:   { bar: '#f43f5e', icon: '#f43f5e', bg: 'rgba(255,255,255,0.97)', text: '#1e293b' },
  success: { bar: '#10b981', icon: '#10b981', bg: 'rgba(255,255,255,0.97)', text: '#1e293b' },
  info:    { bar: '#6366f1', icon: '#6366f1', bg: 'rgba(255,255,255,0.97)', text: '#1e293b' },
};

const ToastContainer = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: 'calc(100% - 32px)',
        maxWidth: 390,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => {
        const s = STYLES[t.type] || STYLES.error;
        return (
          <div
            key={t.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: s.bg,
              borderRadius: 16,
              boxShadow: '0 4px 24px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.08)',
              padding: '12px 14px',
              borderLeft: `4px solid ${s.bar}`,
              pointerEvents: 'all',
              animation: t.exiting ? 'toast-out 0.3s ease forwards' : 'toast-in 0.3s ease forwards',
            }}
          >
            {/* Icon badge */}
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: `${s.bar}18`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontWeight: 700,
                fontSize: 12,
                color: s.icon,
              }}
            >
              {ICONS[t.type]}
            </div>

            {/* Message */}
            <p
              style={{
                flex: 1,
                margin: 0,
                fontSize: 13,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 500,
                color: s.text,
                lineHeight: 1.4,
              }}
            >
              {t.message}
            </p>

            {/* Dismiss */}
            <button
              onClick={() => onDismiss(t.id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 4px',
                fontSize: 16,
                color: '#94a3b8',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>
        );
      })}

      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        @keyframes toast-out {
          from { opacity: 1; transform: translateY(0)   scale(1);    }
          to   { opacity: 0; transform: translateY(-6px) scale(0.97); }
        }
      `}</style>
    </div>
  );
};
